import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowRight, XCircle } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();

  const [state, setState] = useState('polling'); // polling | paid | failed | timeout
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState('failed');
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 10) {
        setState('timeout');
        return;
      }
      attemptsRef.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setData(res.data);
        if (res.data.payment_status === 'paid') {
          setState('paid');
          return;
        }
        if (['failed', 'expired'].includes(res.data.payment_status)) {
          setState('failed');
          return;
        }
      } catch (e) {
        // keep polling until timeout
      }
      setTimeout(poll, 2000);
    };
    poll();

    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <Header />
      <main className="max-w-xl mx-auto px-6 py-24 text-center">
        {state === 'polling' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-orange-100 flex items-center justify-center mb-6">
              <Loader2 size={32} className="text-[#F97316] animate-spin" />
            </div>
            <h1 className="text-[32px] font-extrabold text-stone-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Confirming your payment…
            </h1>
            <p className="mt-3 text-stone-600">This usually takes a couple of seconds. Please don't close the tab.</p>
          </>
        )}

        {state === 'paid' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-6">
              <CheckCircle2 size={32} className="text-emerald-600" strokeWidth={2.2} />
            </div>
            <h1 className="text-[36px] font-extrabold text-stone-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              You're in.
            </h1>
            <p className="mt-3 text-stone-600">
              Welcome to <span className="font-bold text-stone-900">{data?.product_name || 'Ambanji'}</span>. A receipt is on the way to your email.
            </p>
            {data?.amount && (
              <p className="mt-1 text-[13px] text-stone-500">Subscription: ${data.amount.toFixed(2)}/month</p>
            )}
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <button onClick={() => navigate('/')} className="px-6 py-3 text-[13px] font-bold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition inline-flex items-center justify-center gap-2">
                Back to home <ArrowRight size={14} />
              </button>
              <a href="mailto:hello@ambanji.com" className="px-6 py-3 text-[13px] font-bold text-stone-800 border border-stone-300 rounded-lg hover:bg-stone-100 transition">
                Email onboarding
              </a>
            </div>
          </>
        )}

        {(state === 'failed' || state === 'timeout') && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle size={32} className="text-red-600" />
            </div>
            <h1 className="text-[30px] font-extrabold text-stone-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {state === 'timeout' ? 'Still confirming…' : 'Payment could not be confirmed'}
            </h1>
            <p className="mt-3 text-stone-600">
              {state === 'timeout'
                ? "Your payment may still complete in the background. Check your email in a few minutes or contact us if you don't see a receipt."
                : "No charge was made. You can try again from the pricing page."}
            </p>
            <div className="mt-8">
              <Link to="/pricing" className="px-6 py-3 text-[13px] font-bold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition inline-flex items-center gap-2">
                Back to pricing <ArrowRight size={14} />
              </Link>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
